// InboxPopover — Topbar bell popover (iter16) showing a unified personal feed:
// MENTION · ASSIGNED · SLA_WARNING · SLA_BREACH · APPROVAL_NEEDED · 8D_ACTION_NEEDED.
//
// Rendered conditionally by Topbar.jsx; receives the anchor click + close handler.
import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  AlertOctagon,
  AlertTriangle,
  MessageSquare,
  UserPlus,
  CheckCircle2,
  ClipboardList,
  Inbox as InboxIcon,
  TrendingDown,
} from "lucide-react";
import { useInbox, useMarkInboxItemRead, useMarkAllInboxRead, useDeleteInboxItem } from "../api/sitroom";
import { useStore } from "../store";
import { Trash2 } from "lucide-react";

const TYPE_META = {
  SLA_BREACH: { Icon: AlertOctagon, color: "#E24B4A", label: "SLA Breach" },
  SLA_WARNING: { Icon: AlertTriangle, color: "#f3b14a", label: "SLA Warning" },
  MENTION: { Icon: MessageSquare, color: "#9ec3ec", label: "Mention" },
  ASSIGNED: { Icon: UserPlus, color: "#c89adb", label: "Assigned" },
  APPROVAL_NEEDED: { Icon: CheckCircle2, color: "#4ed084", label: "Approval needed" },
  "8D_ACTION_NEEDED": { Icon: ClipboardList, color: "#9ec3ec", label: "8D action" },
  SERVICE_REQUEST_CREATED: { Icon: ClipboardList, color: "#f3b14a", label: "Service request" },
  SERVICE_REQUEST_UPDATED: { Icon: ClipboardList, color: "#9ec3ec", label: "Service request updated" },
  BI_PLAN_ALERT: { Icon: TrendingDown, color: "#E24B4A", label: "BI plan alert" },
};

const relativeTime = (iso) => {
  if (!iso) return "";
  try {
    const then = new Date(iso);
    const diffMs = Date.now() - then.getTime();
    const min = Math.floor(diffMs / 60000);
    if (min < 1) return "just now";
    if (min < 60) return `${min} min ago`;
    const hr = Math.floor(min / 60);
    if (hr < 24) return `${hr}h ago`;
    const days = Math.floor(hr / 24);
    if (days === 1) return "yesterday";
    if (days < 7) return `${days}d ago`;
    return then.toLocaleDateString();
  } catch {
    return "";
  }
};

const dateBucket = (iso) => {
  if (!iso) return "Earlier";
  try {
    const then = new Date(iso);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const yest = new Date(today);
    yest.setDate(yest.getDate() - 1);
    const weekAgo = new Date(today);
    weekAgo.setDate(weekAgo.getDate() - 7);

    if (then >= today) return "Today";
    if (then >= yest) return "Yesterday";
    if (then >= weekAgo) return "This week";
    return "Earlier";
  } catch {
    return "Earlier";
  }
};

export const InboxPopover = ({ onClose }) => {
  const navigate = useNavigate();
  const openPanel = useStore((s) => s.openPanel);
  const openEightDPanel = useStore((s) => s.openEightDPanel);
  const openFmeaPanel = useStore((s) => s.openFmeaPanel);
  const { data: rows = [], isLoading } = useInbox();
  const markRead = useMarkInboxItemRead();
  const markAllRead = useMarkAllInboxRead();
  const deleteItem = useDeleteInboxItem();

  const grouped = useMemo(() => {
    const order = ["Today", "Yesterday", "This week", "Earlier"];
    const buckets = {};
    rows.forEach((r) => {
      const b = dateBucket(r.created_at);
      (buckets[b] = buckets[b] || []).push(r);
    });
    return order.filter((k) => buckets[k]).map((k) => ({ bucket: k, items: buckets[k] }));
  }, [rows]);

  const unreadCount = rows.filter((r) => !r.is_read).length;

  const activate = (item) => {
    if (!item.is_read) markRead.mutate(item.id);
    onClose && onClose();
    if (item.type === "BI_PLAN_ALERT" || item.entity_type === "bi_plan_alert") {
      navigate("/bi/dashboard?tab=alerts");
      return;
    }
    if (item.entity_type === "fmea") {
      navigate("/fmea");
      setTimeout(() => openFmeaPanel(item.entity_id), 60);
    } else if (item.entity_type === "eightd") {
      navigate("/8d");
      setTimeout(() => openEightDPanel(item.entity_id), 60);
    } else if (item.entity_type === "service_request") {
      // Open the parent incident; SR section is inline within it
      if (item.incident_id) {
        navigate("/incidents");
        setTimeout(() => openPanel(item.incident_id), 60);
      }
    } else if (item.incident_id) {
      navigate("/incidents");
      setTimeout(() => openPanel(item.incident_id), 60);
    }
  };

  return (
    <div
      className="absolute right-0 mt-2 rounded-md overflow-hidden"
      style={{
        width: 360,
        background: "#0d0f18",
        border: "1px solid rgba(255,255,255,0.10)",
        boxShadow: "0 8px 24px rgba(0,0,0,0.5)",
        maxHeight: 480,
        display: "flex",
        flexDirection: "column",
      }}
      data-testid="bell-popover"
    >
      {/* Header */}
      <div
        className="flex items-center justify-between px-4 py-3"
        style={{
          borderBottom: "1px solid rgba(255,255,255,0.08)",
          flexShrink: 0,
        }}
      >
        <div className="flex items-center gap-2">
          <span
            style={{
              fontFamily: "Syne, sans-serif",
              fontWeight: 700,
              fontSize: 14,
              color: "#f0f2f8",
              letterSpacing: "0.3px",
            }}
          >
            My Inbox
          </span>
          {unreadCount > 0 && (
            <span
              style={{
                background: "rgba(24,95,165,0.20)",
                border: "1px solid rgba(24,95,165,0.55)",
                color: "#9ec3ec",
                fontFamily: "DM Mono, monospace",
                fontSize: 9,
                padding: "1px 6px",
                borderRadius: 3,
                letterSpacing: "0.8px",
              }}
              data-testid="inbox-unread-pill"
            >
              {unreadCount} UNREAD
            </span>
          )}
        </div>
        {unreadCount > 0 && (
          <button
            onClick={() => markAllRead.mutate()}
            style={{
              background: "transparent",
              border: "none",
              color: "#9ec3ec",
              fontFamily: "DM Mono, monospace",
              fontSize: 10,
              letterSpacing: "0.5px",
              cursor: "pointer",
              padding: 0,
            }}
            data-testid="mark-all-read-btn"
          >
            Mark all read
          </button>
        )}
      </div>

      {/* List */}
      <div className="overflow-y-auto flex-1" style={{ minHeight: 120 }}>
        {isLoading && (
          <div
            className="px-4 py-6 text-center"
            style={{ fontFamily: "DM Mono, monospace", fontSize: 11, color: "#9198b0" }}
            data-testid="inbox-loading"
          >
            Loading…
          </div>
        )}
        {!isLoading && rows.length === 0 && (
          <div
            className="px-4 py-10 text-center flex flex-col items-center gap-2"
            data-testid="inbox-empty"
          >
            <InboxIcon size={28} color="#3d4258" strokeWidth={1.5} />
            <div
              style={{
                fontFamily: "Syne, sans-serif",
                fontWeight: 700,
                fontSize: 14,
                color: "#f0f2f8",
              }}
            >
              You&rsquo;re all caught up
            </div>
            <div
              style={{
                fontFamily: "DM Mono, monospace",
                fontSize: 10,
                color: "#9198b0",
                letterSpacing: "0.4px",
              }}
            >
              No pending actions
            </div>
          </div>
        )}

        {grouped.map(({ bucket, items }) => (
          <div key={bucket} data-testid={`inbox-bucket-${bucket.toLowerCase().replace(/\s/g, "-")}`}>
            <div
              className="px-4 py-1.5"
              style={{
                fontFamily: "DM Mono, monospace",
                fontSize: 9,
                letterSpacing: "1.5px",
                color: "#9198b0",
                textTransform: "uppercase",
                background: "rgba(255,255,255,0.02)",
              }}
            >
              {bucket} · {items.length}
            </div>
            {items.map((n) => {
              const meta = TYPE_META[n.type] || {
                Icon: InboxIcon,
                color: "#9aa1b3",
                label: n.type,
              };
              const Icon = meta.Icon;
              const unread = !n.is_read;
              return (
                <div
                  key={n.id}
                  className="group w-full flex items-start gap-3 px-4 py-2.5 transition-colors hover:bg-white/[0.03]"
                  style={{
                    background: unread ? "rgba(24,95,165,0.06)" : "transparent",
                    borderLeft: unread ? "2px solid #185FA5" : "2px solid transparent",
                    borderBottom: "1px solid rgba(255,255,255,0.04)",
                  }}
                  data-testid={`inbox-item-${n.id}`}
                  data-type={n.type}
                  data-unread={unread ? "1" : "0"}
                >
                  <button
                    type="button"
                    onClick={() => activate(n)}
                    className="flex-1 text-left flex items-start gap-3"
                    style={{ background: "transparent", border: "none", padding: 0, cursor: "pointer" }}
                  >
                    <span
                      className="inline-flex items-center justify-center flex-shrink-0"
                      style={{
                        width: 28,
                        height: 28,
                        borderRadius: 6,
                        background: `${meta.color}1a`,
                        border: `1px solid ${meta.color}55`,
                        color: meta.color,
                        marginTop: 1,
                      }}
                    >
                      <Icon size={14} />
                    </span>
                  <div className="flex-1 min-w-0">
                    <div
                      className="flex items-start gap-2"
                      style={{
                        fontFamily: "Syne, sans-serif",
                        fontSize: 12.5,
                        fontWeight: 700,
                        color: unread ? "#f0f2f8" : "#cfd1d8",
                        lineHeight: 1.3,
                      }}
                    >
                      <span className="flex-1 break-words">{n.title}</span>
                      <span
                        style={{
                          fontFamily: "DM Mono, monospace",
                          fontSize: 9,
                          color: "#6b7388",
                          letterSpacing: "0.4px",
                          fontWeight: 400,
                          whiteSpace: "nowrap",
                        }}
                      >
                        {relativeTime(n.created_at)}
                      </span>
                    </div>
                    <div
                      className="mt-1 break-words"
                      style={{
                        fontFamily: "DM Mono, monospace",
                        fontSize: 10,
                        color: "#9aa1b3",
                        letterSpacing: "0.3px",
                        lineHeight: 1.4,
                      }}
                    >
                      {n.body}
                    </div>
                    {(n.incident_id || n.entity_id) && (
                      <div
                        className="mt-1"
                        style={{
                          fontFamily: "DM Mono, monospace",
                          fontSize: 9,
                          color: "#6b7388",
                          letterSpacing: "0.5px",
                        }}
                      >
                        {n.entity_type !== "incident" && n.entity_id ? `${n.entity_id} · ` : ""}
                        {n.incident_id || ""}
                      </div>
                    )}
                  </div>
                  </button>
                  {/* Hover-delete (iter30) */}
                  <button
                    type="button"
                    aria-label="Delete notification"
                    onClick={(e) => { e.stopPropagation(); deleteItem.mutate(n.id); }}
                    className="opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0"
                    style={{
                      background: "transparent",
                      border: "none",
                      color: "#9198b0",
                      cursor: "pointer",
                      padding: 4,
                      marginTop: 4,
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.color = "#E24B4A")}
                    onMouseLeave={(e) => (e.currentTarget.style.color = "#9198b0")}
                    data-testid={`inbox-delete-${n.id}`}
                  >
                    <Trash2 size={12} />
                  </button>
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
};
